// src/services/api.ts
import axios, { AxiosRequestHeaders, InternalAxiosRequestConfig } from "axios";

/**
 * CRA env var:
 * REACT_APP_API_BASE_URL=http://localhost:5000
 */
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL?.trim() || "http://localhost:5000";

/**
 * Clerk injection points:
 * - setClerkTokenGetter: used by axios interceptor to attach Authorization: Bearer <token>
 * - setClerkUserIdGetter: optional; but backend should derive user_id from token (best practice)
 */
let clerkTokenGetter: null | (() => Promise<string | null>) = null;
let clerkUserIdGetter: null | (() => string | null) = null;

export const setClerkTokenGetter = (getter: (() => Promise<string | null>) | null) => {
  clerkTokenGetter = getter;
};

export const setClerkUserIdGetter = (getter: (() => string | null) | null) => {
  clerkUserIdGetter = getter;
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/**
 * Attach Clerk session token on every request (when signed in).
 * This is what your Flask backend expects: Authorization: Bearer <token>
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      if (clerkTokenGetter) {
        const token = await clerkTokenGetter();

        if (token && token.trim().length > 0) {
          const currentHeaders: AxiosRequestHeaders =
            config.headers || ({} as AxiosRequestHeaders);

          config.headers = {
            ...currentHeaders,
            Authorization: `Bearer ${token}`,
          } as AxiosRequestHeaders;
        }
      }
    } catch (err) {
      // warn and proceed without token
      console.warn("Error getting Clerk token:", err);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

/**
 * IMPORTANT:
 * Your backend should NOT trust a user_id provided by the client.
 * Keep this only for compatibility in places where legacy code passes user_id;
 * the backend should ignore it and always use token->sub.
 */
const getEffectiveUserId = (provided?: string) => {
  if (provided) return provided;
  if (clerkUserIdGetter) return clerkUserIdGetter() ?? "anonymous";
  return "anonymous";
};

function processSSEChunk(
  chunk: string,
  handlers: {
    onToken?: (token: string) => void;
    onMeta?: (meta: { session_id: string; session_title: string }) => void;
    onDone?: () => void;
    onError?: (error: string) => void;
  },
) {
  const dataLine = chunk.replace(/^data: /, "").trim();
  if (!dataLine) return;
  try {
    const parsed = JSON.parse(dataLine);
    if (parsed.type === "token") { handlers.onToken?.(parsed.content); }
    else if (parsed.type === "meta") { handlers.onMeta?.(parsed); }
    else if (parsed.type === "done") { handlers.onDone?.(); }
    else if (parsed.type === "error") { handlers.onError?.(parsed.message); }
  } catch {
    // skip malformed chunks
  }
}

async function readSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  handlers: {
    onToken?: (token: string) => void;
    onMeta?: (meta: { session_id: string; session_title: string }) => void;
    onDone?: () => void;
    onError?: (error: string) => void;
  },
) {
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n\n");
    buffer = lines.pop() || "";

    for (const chunk of lines) {
      processSSEChunk(chunk, handlers);
    }
  }
}

export const apiService = {
  // ------------------------------------
  // Backend chat session controls
  // ------------------------------------
  createSession: () => apiClient.post("/api/chat/sessions/create"),

  createChatSession: (title?: string) =>
    apiClient.post("/api/chat/sessions/create", title ? { title } : {}),

  endSession: () => apiClient.post("/end_session"),

  /**
   * If you don't have /logout on backend, remove this.
   * Prefer Clerk signOut() on the frontend.
   */
  logout: async () => {
    // no-op; keep to prevent old imports from breaking
    // You should call Clerk signOut() from UI code.
    return { data: { status: "ok" } };
  },

  // -------------------------
  // Tariff Assistant
  // -------------------------
  queryTariff: (userQuery: string, sessionId?: string, newSession: boolean = false) =>
    apiClient.get("/query_tariff", {
      params: {
        user_query: userQuery,
        new_session: newSession ? "true" : "false",
        ...(sessionId && { session_id: sessionId }),
      },
      timeout: 60000,
    }),

  queryTariffStream: async (
    userQuery: string,
    sessionId?: string,
    newSession: boolean = false,
    onToken?: (token: string) => void,
    onMeta?: (meta: { session_id: string; session_title: string }) => void,
    onDone?: () => void,
    onError?: (error: string) => void,
  ) => {
    const authToken = clerkTokenGetter ? await clerkTokenGetter() : null;

    const params = new URLSearchParams({
      user_query: userQuery,
      new_session: newSession ? "true" : "false",
    });
    if (sessionId) params.set("session_id", sessionId);

    const url = `${API_BASE_URL}/query_tariff?${params.toString()}`;

    const response = await fetch(url, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: "Stream request failed" }));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("No response body");

    await readSSEStream(reader, { onToken, onMeta, onDone, onError });
  },

  // -------------------------
  // Tariff Calculator
  // -------------------------
  calculateTariff: (
    params: {
      product_name?: string;
      hts_code?: string;
      country_of_origin: string;
      value: number;
      quantity: number;
      user_id?: string;
    }
  ) =>
    apiClient.get("/calc_tariff", {
      params: {
        product_name: params.product_name,
        hts_code: params.hts_code,
        country_of_origin: params.country_of_origin,
        value: params.value,
        quantity: params.quantity,
        // Legacy compatibility only — backend should ignore and use token
        user_id: getEffectiveUserId(params.user_id),
      },
      timeout: 120000,
    }),

  getRouteScenarios: (limit: number = 500) =>
    apiClient.get("/route/saved-scenarios", {
      params: { limit },
      timeout: 30000,
    }),

  saveRouteScenario: (scenario: {
    id: string;
    product: string;
    destination: string;
    quantity: string;
    supplier: string;
    strategy: string;
    savedAt: string;
    routeName?: string;
    from?: string;
    to?: string;
    totalCost?: string;
    riskPct?: number;
    riskColor?: string;
    tariffImpact?: string | null;
    mode?: string;
    modeLabel?: string;
    modeBadgeBg?: string;
    modeBadgeIconColor?: string;
    eta?: string;
  }) => apiClient.post("/route/saved-scenarios", scenario, { timeout: 30000 }),

  deleteRouteScenario: (scenarioId: string) =>
    apiClient.delete(`/route/saved-scenarios/${encodeURIComponent(scenarioId)}`, {
      timeout: 30000,
    }),

  /**
   * NEW: Persist tariff calculation only when user clicks "Save"
   * Backend derives user_id from Clerk token.
   */
  saveTariffCalculation: (calculationData: {
    product_name?: string;
    hts_code: string;
    country_of_origin: string;
    import_value: number;
    import_quantity: number;
    total_duty_amount: number;
    result: any;
  }) => apiClient.post("/save_tariff_calculation", calculationData, { timeout: 30000 }),

  getTariffCalculationsHistory: (user_id?: string, limit: number = 5) =>
    apiClient.get("/tariff_calculations_history", {
      params: {
        // Legacy compatibility only — backend should ignore and use token
        user_id: getEffectiveUserId(user_id),
        limit,
      },
      timeout: 30000,
    }),

  getTariffInputs: () => apiClient.get("/tariff_inputs", { timeout: 30000 }),

  // -------------------------
  // HTS Code Requests
  // -------------------------
  submitHtsRequest: (data: {
    product_name: string;
    part_number?: string;
    part_description?: string;
    country_of_origin?: string;
    importance?: string;
    additional_comments?: string;
    submitted_by_name?: string;
  }) => apiClient.post("/api/hts-requests", data, { timeout: 30000 }),

  getHtsRequests: () => apiClient.get("/api/hts-requests", { timeout: 30000 }),

  updateHtsRequestStatus: (requestId: number, status: string) =>
    apiClient.patch(`/api/hts-requests/${requestId}/status`, { status }, { timeout: 15000 }),

  // -------------------------
  // Chat history
  // -------------------------
  getChatMessages: (sessionId?: string, limit?: number) =>
    sessionId
      ? apiClient.get(`/api/chat/sessions/${sessionId}/messages/list`, {
          params: {
            ...(typeof limit === "number" ? { limit } : {}),
          },
          timeout: 30000,
        })
      : apiClient.get("/get_chat_messages", {
          params: {
            ...(typeof limit === "number" ? { limit } : {}),
          },
          timeout: 30000,
        }),

  getChatSessions: (limit: number = 10) =>
    apiClient.get("/api/chat/sessions/list", {
      params: { limit },
      timeout: 30000,
    }),

  deleteChatSession: (sessionId: string) =>
    apiClient.delete(`/api/chat/sessions/${sessionId}`, { timeout: 30000 }),


  // Route optimization
  getProducts: () =>
    apiClient.get("/dropdown/products", { timeout: 30000 }),


  getDestinations: (product_name: string) =>
    apiClient.get("/dropdown/destination-nodes", { params: { product_name }, timeout: 30000 }),
  getRoute: (params: {
    product_name: string;
    supplier_name?: string;
    destination_location?: string;
    opt?: string;
    qty?: number;
  }) => {
    const sn = params.supplier_name?.trim();
    return apiClient.get("/get_route", {
      params: {
        product_name: params.product_name,
        ...(sn ? { supplier_name: sn } : {}),
        destination_location: params.destination_location,
        opt: params.opt ?? "cost",
        ...(params.qty && params.qty > 0 ? { qty: params.qty } : {}),
      },
      timeout: 30000,
    });
  },

  getRouteWaypoints: (
    source: { lat: number; lng: number },
    target: { lat: number; lng: number },
    mode: string
  ) =>
    apiClient.get("/api/route-waypoints", {
      params: {
        source_lat: source.lat,
        source_lng: source.lng,
        target_lat: target.lat,
        target_lng: target.lng,
        mode,
      },
      timeout: 30000,
    }),

  // Capacity feasibility

  checkCapacity: (params: {
    product_name: string;
    qty: number;
    opt?: string;
    supplier_name?: string;
    destination?: string;
  }) =>
    apiClient.get("/route/capacity-check", {
      params: {
        product_name: params.product_name,
        qty: params.qty,
        opt: params.opt ?? "cost",
        ...(params.supplier_name ? { supplier_name: params.supplier_name } : {}),
        ...(params.destination ? { destination: params.destination } : {}),
      },
      timeout: 30000,
    }),

  /** Fetch suppliers for a product, optionally filtered by capacity (qty).
   *  When qty is provided, only suppliers with supplier_capacity >= qty are returned.
   *  When qty is omitted, all suppliers for the product are returned.
   */
  getFeasibleSuppliers: (params: {
    product_name: string;
    qty?: number;
    destination?: string;
  }) =>
    apiClient.get("/route/feasible-suppliers", {
      params: {
        product_name: params.product_name,
        ...(params.qty && params.qty > 0 ? { qty: params.qty } : {}),
        ...(params.destination ? { destination: params.destination } : {}),
      },
      timeout: 30000,
    }),

  getRiskBreakdown: (params: { shipment_id?: string; route_id?: string }) =>
    apiClient.get("/risk/breakdown", {
      params: {
        ...(params.shipment_id ? { shipment_id: params.shipment_id } : {}),
        ...(params.route_id ? { route_id: params.route_id } : {}),
      },
      timeout: 30000,
    }),

  getRiskRecommendations: (params: { shipment_id?: string; route_id?: string }) =>
    apiClient.get("/risk/recommendations", {
      params: {
        ...(params.shipment_id ? { shipment_id: params.shipment_id } : {}),
        ...(params.route_id ? { route_id: params.route_id } : {}),
      },
      timeout: 30000,
    }),


  // -------------------------
  // Dashboard (real backend endpoint in your api.py)
  // -------------------------
  getPredictiveOverview: (filters?: {
    product?: string;
    supplier?: string;
    source_country?: string;
    destination_city?: string;
    threshold?: number;
    limit?: number;
    offset?: number;
    sort?: string;
    group_by?: string;
    skip_shap?: boolean;
    date_from?: string;
    date_to?: string;
    only_delayed?: boolean;
    route_id?: string;
    search?: string;
    predicted_class?: number;
    risk_level?: string;
  }) => {
    const params: any = { ...filters };
    if (!params?.group_by) delete params.group_by;

    // Normalize boolean to backend style if needed
    if (typeof params.only_delayed === "boolean") {
      params.only_delayed = params.only_delayed ? "true" : "false";
    }

    return apiClient.get("/dashboard", {
      params,
      timeout: 30000,
    });
  },

  // -------------------------
  // Placeholders (keep if your UI still calls them)
  // NOTE: If these routes do not exist on backend, they will 404.
  // Prefer removing these calls from the UI as you wire real endpoints.
  // -------------------------
  getDashboardStats: () => apiClient.get("/dashboard-stats"),
  getActiveAlerts: () => apiClient.get("/active-alerts"),
  sendChatMessage: (message: string) => apiClient.post("/agentic-chat", { message }),
  getAiRiskExpert: (query: string) => apiClient.post("/ai-risk-expert", { query }),

  getTariffData: () => apiClient.get("/route-analysis-stats"),
  getCostAnalytics: () => apiClient.get("/analytics-overview"),

  getRiskPredictions: () => apiClient.get("/predict-risk"),
  getAnalyticsOverview: () => apiClient.get("/analytics-overview"),
  getFailureAnalysis: () => apiClient.get("/shipment-failure-root-cause"),

  analyzeRoute: (data: any) => apiClient.post("/analyze-route", data),
  getRouteStats: () => apiClient.get("/route-analysis-stats"),
  getNetworkStats: () => apiClient.get("/network-stats"),
  getRoutePerformance: () => apiClient.get("/route-performance-analysis"),

  getFilterOptions: (filters?: {
    product?: string;
    supplier?: string;
    destination_city?: string;
    source_country?: string;
  }) => {
    const params: Record<string, string> = {};
    if (filters?.product) params.product = filters.product;
    if (filters?.supplier) params.supplier = filters.supplier;
    if (filters?.destination_city) params.destination_city = filters.destination_city;
    if (filters?.source_country) params.source_country = filters.source_country;
    return apiClient.get("/filter-options", { params, timeout: 30000 });
  },

  // -------------------------
  // Databricks KPIs (group counts used for Total Active Orders, At Risk, etc.)
  // -------------------------
  getDatabricksKpis: () =>
    apiClient.get("/api/databricks/dashboard/kpis", { timeout: 30000 }),

  getOnTimePerformance: () =>
    apiClient.get("/api/databricks/dashboard/on-time-performance", { timeout: 30000 }),

  getAtRiskOrders: (limit = 20) =>
    apiClient.get("/api/databricks/dashboard/kpi-rows/AT_RISK", {
      params: { limit },
      timeout: 30000,
    }),

  // -------------------------
  // Weather alerts (Databricks volume → Geographical Alerts map)
  // -------------------------
  getWeatherAlerts: (options?: { severeOnly?: boolean; limit?: number }) =>
    apiClient.get("/api/databricks/weather-alerts", {
      params: {
        ...(options?.severeOnly ? { severe_only: "true" } : {}),
        ...(options?.limit ? { limit: options.limit } : {}),
      },
      timeout: 30000,
    }),

  getPortWatchHeatmap: (options?: { lookbackDays?: number; baselineDays?: number }) =>
    apiClient.get("/api/portwatch/heatmap", {
      params: {
        ...(options?.lookbackDays ? { lookback_days: options.lookbackDays } : {}),
        ...(options?.baselineDays ? { baseline_days: options.baselineDays } : {}),
      },
      timeout: 30000,
    }),

  getShipmentDetails: (shipmentId: string) =>
    apiClient.get(`/shipment/${shipmentId}/details`, { timeout: 60000 }),

  explainShipment: async (shipmentId: string) => {
    const response = await apiClient.get(`/shipment/${shipmentId}/details`, {
      timeout: 60000,
    });
    return response.data;
  },

  getAiRecommendations: (shipmentId: string) =>
    apiClient.get(`/shipment/${shipmentId}/ai-recommendations`, { timeout: 30000 }),

  // -------------------------
  // A.8.10 - User data rights
  // -------------------------
  exportMyData: () =>
    apiClient.get("/api/user/data/export", { timeout: 60000 }),
  deleteMyData: () =>
    apiClient.delete("/api/user/data", { timeout: 60000 }),

  // -------------------------
  // Admin Panel
  // -------------------------
  getMyRole: () => apiClient.get("/api/admin/me/role", { timeout: 10000 }),
  getMyModules: () => apiClient.get("/api/admin/me/modules", { timeout: 10000 }),
  getAuditLog: (limit: number = 10, offset: number = 0) =>
    apiClient.get("/api/admin/audit-log", { params: { limit, offset }, timeout: 30000 }),

  // Roles (default module permissions per role)
  getAdminRoles: () => apiClient.get("/api/admin/roles", { timeout: 15000 }),
  setRoleModules: (roleName: string, modules: Record<string, boolean>) =>
    apiClient.put(`/api/admin/roles/${encodeURIComponent(roleName)}/modules`, { modules }, { timeout: 15000 }),

  // Users
  getAdminUsers: () => apiClient.get("/api/admin/users", { timeout: 30000 }),
  getAdminUser: (clerkUserId: string) =>
    apiClient.get(`/api/admin/users/${clerkUserId}`, { timeout: 15000 }),
  updateUserRole: (clerkUserId: string, role: string) =>
    apiClient.put(`/api/admin/users/${clerkUserId}/role`, { role }, { timeout: 15000 }),
  deactivateUser: (clerkUserId: string) =>
    apiClient.delete(`/api/admin/users/${clerkUserId}`, { timeout: 15000 }),
  activateUser: (clerkUserId: string) =>
    apiClient.post(`/api/admin/users/${clerkUserId}/activate`, {}, { timeout: 15000 }),
  recordUserLogin: () =>
    apiClient.post("/api/users/record-login", {}, { timeout: 15000 }),
  getUserModules: (clerkUserId: string) =>
    apiClient.get(`/api/admin/users/${clerkUserId}/modules`, { timeout: 15000 }),
  setUserModules: (clerkUserId: string, modules: Record<string, boolean>) =>
    apiClient.put(
      `/api/admin/users/${clerkUserId}/modules`,
      { modules },
      { timeout: 15000 },
    ),
  addUserToTeam: (clerkUserId: string, teamId: number) =>
    apiClient.post(`/api/admin/users/${clerkUserId}/teams`, { team_id: teamId }, { timeout: 15000 }),
  removeUserFromTeam: (clerkUserId: string, teamId: number) =>
    apiClient.delete(`/api/admin/users/${clerkUserId}/teams/${teamId}`, { timeout: 15000 }),

  // Teams
  getAdminTeams: () => apiClient.get("/api/admin/teams", { timeout: 30000 }),
  getAdminTeam: (teamId: number) =>
    apiClient.get(`/api/admin/teams/${teamId}`, { timeout: 15000 }),
  createAdminTeam: (name: string, description: string) =>
    apiClient.post("/api/admin/teams", { name, description }, { timeout: 15000 }),
  updateAdminTeam: (teamId: number, name: string, description: string) =>
    apiClient.put(`/api/admin/teams/${teamId}`, { name, description }, { timeout: 15000 }),
  deleteAdminTeam: (teamId: number) =>
    apiClient.delete(`/api/admin/teams/${teamId}`, { timeout: 15000 }),
  addTeamMember: (teamId: number, clerkUserId: string) =>
    apiClient.post(`/api/admin/teams/${teamId}/members`, { clerk_user_id: clerkUserId }, { timeout: 15000 }),
  removeTeamMember: (teamId: number, clerkUserId: string) =>
    apiClient.delete(`/api/admin/teams/${teamId}/members/${clerkUserId}`, { timeout: 15000 }),

  // Workspaces
  getAdminWorkspaces: () => apiClient.get("/api/admin/workspaces", { timeout: 30000 }),
  getAdminWorkspace: (workspaceId: number) =>
    apiClient.get(`/api/admin/workspaces/${workspaceId}`, { timeout: 15000 }),
  createAdminWorkspace: (name: string, description: string) =>
    apiClient.post("/api/admin/workspaces", { name, description }, { timeout: 15000 }),
  updateAdminWorkspace: (workspaceId: number, name: string, description: string) =>
    apiClient.put(`/api/admin/workspaces/${workspaceId}`, { name, description }, { timeout: 15000 }),
  deleteAdminWorkspace: (workspaceId: number) =>
    apiClient.delete(`/api/admin/workspaces/${workspaceId}`, { timeout: 15000 }),
  assignTeamToWorkspace: (workspaceId: number, teamId: number) =>
    apiClient.post(`/api/admin/workspaces/${workspaceId}/teams`, { team_id: teamId }, { timeout: 15000 }),
  removeTeamFromWorkspace: (workspaceId: number, teamId: number) =>
    apiClient.delete(`/api/admin/workspaces/${workspaceId}/teams/${teamId}`, { timeout: 15000 }),

  // -------------------------
  // A.8.10 - Admin retention actions
  // -------------------------
  adminExportRetentionData: (payload: {
    table_name: string;
    date_column?: string;
    days?: number;
  }) =>
    apiClient.post("/api/v1/data/export", payload, { timeout: 60000 }),
  adminPurgeRetentionData: (payload: {
    days?: number;
    tables_config?: Array<{ table: string; date_column: string }>;
  }) =>
    apiClient.delete("/api/v1/data/purge", { data: payload, timeout: 60000 }),
};

export default apiClient;
